const MAX_TRANSLATION_CHAR_LIMIT = 20000;

// Regex patterns for translation skipping - mirroring backend GoogleTranslateService
const EMAIL_REGEX = /^[\w+\-.]+@[a-z\d-]+(\.[a-z\d-]+)*\.[a-z]+$/i;
const URL_REGEX = /^https?:\/\/[^\s]+$/i;
const DOMAIN_REGEX =
  /^(?:www\.)?[a-z0-9]+(?:-[a-z0-9]+)*(?:\.[a-z0-9]+(?:-[a-z0-9]+)*)*\.[a-z]{2,}$/i;
const STREET_TYPES =
  '(?:Street|St|Avenue|Ave|Road|Rd|Boulevard|Blvd|Drive|Dr|Lane|Ln|Court|Ct|Place|Pl|Way|Terrace|Ter)';
const ADDRESS_REGEX = new RegExp(`^\\d+\\s+[A-Za-z\\s]+${STREET_TYPES}$`, 'i');
const DATE_MMDD_REGEX = /^\d{1,2}\/\d{1,2}\/\d{2,4}$/;
const DATE_YYYY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
const TIME_REGEX = /^\d{1,2}:\d{2}(?::\d{2})?(?:\s?[AP]M)?$/i;
const SPECIAL_CHARS_REGEX = /^[\d\s!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]+$/;
const LETTERS_REGEX = /[A-Za-z]/;

/**
 * Logs translation skipping information only in development/test environments
 * @param {string} reason - The reason for skipping translation
 * @param {string} text - The text that was skipped
 */
function logTranslationSkip(reason, text) {
  if (process.env.NODE_ENV === 'development' || process.env.NODE_ENV === 'test') {
    console.log(`[Translation Skip] ${reason}: ${text}`);
  }
}

/**
 * Checks if a string should be skipped for translation based on various criteria.
 * Mirrors the backend GoogleTranslateService.should_skip_translation? logic.
 *
 * @param {string} text - The text to check
 * @returns {boolean} - True if the text should be skipped, false otherwise
 */
export function shouldSkipTranslation(text) {
  if (!text || text.trim() === '') {
    logTranslationSkip('Text is blank or nil', text);
    return true;
  }

  if (text.length > MAX_TRANSLATION_CHAR_LIMIT) {
    logTranslationSkip(
      `Text exceeds character limit of ${MAX_TRANSLATION_CHAR_LIMIT} characters (length: ${text.length})`,
      text,
    );
    return true;
  }

  // Check for email addresses
  if (EMAIL_REGEX.test(text)) {
    logTranslationSkip('Text appears to be an email address', text);
    return true;
  }

  // Check for URLs
  if (URL_REGEX.test(text)) {
    logTranslationSkip('Text appears to be a URL', text);
    return true;
  }

  // Check for website domains (e.g., google.com, site.net, etc.)
  if (DOMAIN_REGEX.test(text)) {
    logTranslationSkip('Text appears to be a website domain', text);
    return true;
  }

  // Check for street addresses (common patterns)
  // Matches: 123 Main St, 123 Main Street, 123 N Main St, etc.
  if (ADDRESS_REGEX.test(text)) {
    logTranslationSkip('Text appears to be a street address', text);
    return true;
  }

  // Check for dates in common formats
  if (DATE_MMDD_REGEX.test(text)) {
    logTranslationSkip('Text appears to be a date (MM/DD/YYYY format)', text);
    return true;
  }
  if (DATE_YYYY_REGEX.test(text)) {
    logTranslationSkip('Text appears to be a date (YYYY-MM-DD format)', text);
    return true;
  }

  // Check for times
  if (TIME_REGEX.test(text)) {
    logTranslationSkip('Text appears to be a time', text);
    return true;
  }

  // Check for strings that contain only numbers and special characters (no letters)
  if (SPECIAL_CHARS_REGEX.test(text) && !LETTERS_REGEX.test(text)) {
    logTranslationSkip('Text contains only numbers and special characters', text);
    return true;
  }

  return false;
}

/**
 * Gets the maximum translation character limit.
 *
 * @returns {number} - The maximum character limit for translations
 */
export function getMaxTranslationCharLimit() {
  return MAX_TRANSLATION_CHAR_LIMIT;
}
